import React, { useEffect, useState } from 'react';
import { Home, ArrowLeft } from 'lucide-react';

/**
 * Página 404 customizada no padrão visual do Monitor Fiscal.
 * Usa Tailwind CSS + dark mode por classe `dark` no <html>.
 * Exibe o mascote OPS flutuando com animação e botões de navegação.
 */
export default function NotFoundPage() {
  // Respeita o tema atual do app (salvo no localStorage)
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem('theme') === 'dark'
  );

  // Sincroniza com a classe `dark` no <html> caso mude externamente
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Aplica a classe de tema no html ao montar (garante consistência visual)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center p-4 font-sans">
      <div className="flex flex-col items-center text-center max-w-md w-full">

        {/* Badge de erro */}
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-8
          bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400
          border border-red-200 dark:border-red-800/50">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400 animate-pulse" />
          Erro 404
        </span>

        {/* Mascote OPS com animação de flutuação */}
        <div className="relative mb-8">
          {/* Halo de fundo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-48 h-48 rounded-full bg-blue-500/10 dark:bg-blue-400/10 blur-2xl animate-pulse" />
          </div>

          {/* Imagem do mascote */}
          <img
            src="/mascot-ops.png"
            alt="Mascote OPS — página não encontrada"
            className="relative w-44 h-44 object-contain drop-shadow-xl select-none"
            style={{ animation: 'mascot-float 4s ease-in-out infinite' }}
            draggable={false}
          />
        </div>

        {/* Número grande decorativo */}
        <p className="text-8xl font-black tracking-tight text-gray-200 dark:text-gray-700/60 leading-none mb-2 select-none">
          404
        </p>

        {/* Título */}
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
          Página não encontrada
        </h1>

        {/* Descrição */}
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-xs">
          A rota que você tentou acessar não existe ou foi removida.
          Verifique o endereço ou volte ao início.
        </p>

        {/* Ações */}
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <a
            href="/"
            id="btn-404-voltar-inicio"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold
              bg-blue-600 hover:bg-blue-700 active:scale-95 text-white
              shadow-md shadow-blue-500/20 hover:shadow-blue-500/30
              transition-all duration-150"
          >
            <Home size={15} />
            Voltar ao início
          </a>

          <button
            id="btn-404-pagina-anterior"
            onClick={() => window.history.back()}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold
              bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95
              text-gray-600 dark:text-gray-300
              border border-gray-200 dark:border-gray-700
              shadow-sm transition-all duration-150"
          >
            <ArrowLeft size={15} />
            Página anterior
          </button>
        </div>

        {/* Rodapé */}
        <p className="mt-10 text-xs text-gray-400 dark:text-gray-600">
          Monitor Fiscal · Extrator SEFAZ
        </p>
      </div>

      {/* Animação CSS da flutuação do mascote */}
      <style>{`
        @keyframes mascot-float {
          0%, 100% { transform: translateY(0px) rotate(-1.5deg); }
          50%       { transform: translateY(-14px) rotate(1.5deg); }
        }
      `}</style>
    </div>
  );
}
